import { ActionLogParameters } from './ActionLogParameters';
import type { Timestamp } from '@framework/Timestamp';
import { isEqual } from 'lodash-es';
import type { FrontendEventName, ActionEventLogJSON, AuthSessionId } from 'balus-common';
import { getClientId } from '@framework/app';

export class ActionLog {
  private constructor(
    private readonly eventName: FrontendEventName,
    private readonly eventParams: ActionLogParameters,
    private readonly ip: string | null,
    private readonly sessionId: AuthSessionId | null,
    private readonly userId: string | null,
    private readonly userProperties: ActionLogParameters,
    private readonly extraPayload: Record<string, unknown>
  ) {}

  static buildNew(
    eventName: FrontendEventName,
    eventParams: ActionEventLogJSON['eventParams'],
    ip: string | null,
    sessionId: AuthSessionId | null | undefined,
    userId: string | null | undefined,
    userProperties: ActionEventLogJSON['userProperties'],
    extraPayload?: Record<string, unknown>
  ): ActionLog {
    return new ActionLog(
      eventName,
      ActionLogParameters.buildFrom(eventParams),
      ip,
      sessionId || null,
      userId || null,
      ActionLogParameters.buildFrom(userProperties),
      extraPayload || {}
    );
  }

  dump(): Omit<Omit<ActionEventLogJSON, 'eventId'>, 'eventTimestamp'> {
    const { eventName, eventParams, ip, sessionId, userId, userProperties, extraPayload } = this;

    return {
      clientId: getClientId(),
      eventName: eventName,
      eventSource: 'frontend',
      eventParams: eventParams.dump(),
      extraPayload: { ...extraPayload },
      ip,
      sessionId,
      userId,
      userProperties: userProperties.dump(),
    };
  }

  /**
   * RTDBへの保存用のJSONシリアライズ結果を返す
   *
   * @param eventId {string} イベントの識別子
   * @param now {Timestamp} 現在時刻の Timestamp オブジェクト
   * @deprecated action-log データベースへの保存廃止時にこのメソッドも削除する
   */
  dumpToSave(eventId: string, now: Timestamp): Omit<ActionEventLogJSON, 'eventTimestamp'> & { eventTimestamp: string } {
    const dump = this.dump();
    return {
      ...dump,
      eventId,
      eventTimestamp: now.toISOString(),
    };
  }

  isEqual(other: ActionLog): boolean {
    return isEqual(this.dump(), other.dump());
  }
}
